import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'

import { APP_BASE_PATH } from '@/app.const'
import { BillingGuard } from '@/components/auth/billing-guard.container'
import { OrgRedirect } from '@/components/auth/org-redirect.container'
import { OrganizationGuard } from '@/components/auth/organization-guard.container'
import { ProtectedRoute } from '@/components/auth/protected-route.container'
import { AppLayout } from '@/components/layout/app-layout.container'
import { agencyRoutes, orgRoutes, rootRoutes } from '@/routes/org-routes.const'
import { AgenciesPage } from './agencies/agencies.page'
import { AgenciesCreatePage } from './agencies/agencies-create.page'
import { AgencyDetailPage } from './agencies/agency-detail.page'
import { AgencyEditPage } from './agencies/agency-edit.page'
import { SignInPage } from './auth/sign-in.page'
import { SignUpPage } from './auth/sign-up.page'
import { ChatPage } from './chat/chat.page'
import { DeepLinkPage } from './go/deep-link.page'
import { OAuthCallbackPage } from './oauth-callback/oauth-callback.page'
import { OnboardingPage } from './onboarding/onboarding.page'
import { OrgSettingsPage } from './org-settings/org-settings.page'
import { ProfilePage } from './profile/profile.page'

const routes: RouteObject[] = [
  {
    path: `/${rootRoutes.signIn}`,
    element: <SignInPage />,
  },
  {
    path: `/${rootRoutes.signUp}`,
    element: <SignUpPage />,
  },

  {
    path: `/${rootRoutes.onboarding}`,
    element: (
      <ProtectedRoute>
        <OnboardingPage />
      </ProtectedRoute>
    ),
  },

  {
    path: '/',
    element: (
      <ProtectedRoute>
        <OrgRedirect />
      </ProtectedRoute>
    ),
  },

  {
    path: '/oauth/callback',
    element: <OAuthCallbackPage />,
  },

  {
    path: '/deeplink/chat/:agencySlug',
    element: <DeepLinkPage />,
  },

  {
    path: '/:orgSlug',
    element: (
      <ProtectedRoute>
        <OrganizationGuard />
      </ProtectedRoute>
    ),
    children: [
      {
        element: <BillingGuard />,
        children: [
          {
            element: <AppLayout />,
            children: [
              {
                index: true,
                element: <ChatPage />,
              },
              {
                path: orgRoutes.chat,
                element: <ChatPage />,
              },
              {
                path: `${orgRoutes.chat}/:agencySlug`,
                element: <ChatPage />,
              },
              {
                path: `${orgRoutes.chat}/:agencySlug/:conversationId`,
                element: <ChatPage />,
              },
              {
                path: `${orgRoutes.conversation}/:conversationId`,
                element: <ChatPage />,
              },
              {
                path: orgRoutes.settings,
                element: <OrgSettingsPage />,
              },
              {
                path: `${orgRoutes.settings}/:settingsView`,
                element: <OrgSettingsPage />,
              },
              {
                path: `${orgRoutes.settings}/:settingsView/:providerSlug`,
                element: <OrgSettingsPage />,
              },
              {
                path: orgRoutes.profile,
                element: <ProfilePage />,
              },
              {
                path: `${orgRoutes.profile}/:profileView`,
                element: <ProfilePage />,
              },
              {
                path: orgRoutes.agencies,
                element: <Navigate to={agencyRoutes.explore} replace />,
              },
              {
                path: `${orgRoutes.agencies}/${agencyRoutes.explore}`,
                element: <AgenciesPage />,
              },
              {
                path: `${orgRoutes.agencies}/${agencyRoutes.explore}/:agencySlug`,
                element: <AgencyDetailPage />,
              },
              {
                path: `${orgRoutes.agencies}/${agencyRoutes.templates}`,
                element: <AgenciesPage />,
              },
              {
                path: `${orgRoutes.agencies}/${agencyRoutes.studio}`,
                element: <AgenciesPage />,
              },
              {
                path: `${orgRoutes.agencies}/${agencyRoutes.studio}/${agencyRoutes.new}`,
                element: <AgenciesCreatePage />,
              },
              {
                path: `${orgRoutes.agencies}/${agencyRoutes.studio}/:agencySlug`,
                element: <AgencyDetailPage />,
              },
              {
                path: `${orgRoutes.agencies}/${agencyRoutes.studio}/:agencySlug/${agencyRoutes.edit}`,
                element: <AgencyEditPage />,
              },
              {
                path: `${orgRoutes.agencies}/${agencyRoutes.organization}`,
                element: <AgenciesPage />,
              },
              {
                path: `${orgRoutes.agencies}/${agencyRoutes.organization}/:agencySlug`,
                element: <AgencyDetailPage />,
              },
            ],
          },
        ],
      },
    ],
  },
]

export const router = createBrowserRouter(routes, {
  basename: APP_BASE_PATH,
})
